import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import useResizeObserver from '@/hooks/useResizeObserver';
import { useChartRow } from '../contexts/ChartRowContext';
import { calculateChartScale } from '@/utils/chartScaling';
import AboutDataCard from '@/components/shared/AboutTheData';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { downloadChart } from '@/utils/chartDownload';
import { chartsDataDefinitions } from '../data/PeerComparisonAboutTheDataDefs';
import { DownloadOptionsPopup } from './DownloadOptionsPopup';
import { ShareOptionsPopup } from './ShareOptionsPopup';
import { useShareUrl } from '@/hooks/useShareUrl';

interface ChartData {
  name: string;
  value: string;
  percentage?: number;
  color: string;
}

interface ChartCardProps {
  category: string;
  title: string;
  subtitle: string;
  mainValue: string;
  mainLocation: string;
  rank: string;
  data: ChartData[];
  xAxisLabel: string;
  yAxisLabels: string[];
  rowIndex: number;
  cardIndex: number;
  onInfoClick?: () => void;
}

// Calculate tick values based on data type and range
const calculateTicks = (data: ChartData[], title: string): { value: number; label: string }[] => {
  // Handle empty data case
  if (!data?.length) {
    return [{ value: 0, label: '0' }];
  }

  // Extract percentages and determine data type
  const percentages = data.map(item => item.percentage || 0);
  const hasPercentage = data.some(d => d.value?.includes('%'));
  const hasX = data.some(d => d.value?.includes('x'));
  const hasUnits = data.some(d => d.value?.toLowerCase().includes('unit'));
  const hasYears = data.some(d => d.value?.toLowerCase().includes('year'));

  // Determine the data type for our scaling utility
  let dataType: 'percentage' | 'ratio' | 'unit' | 'year' | 'custom' = 'custom';
  if (hasPercentage) dataType = 'percentage';
  else if (hasX) dataType = 'ratio';
  else if (hasUnits) dataType = 'unit';
  else if (hasYears) dataType = 'year';

  // Special configuration for population growth chart
  const isPopulationGrowth = title === "Population Growth";

  // Use our new scaling utility
  const scale = calculateChartScale(percentages, {
    tickCount: 5,
    padding: 0.1,
    forceZeroMin: !isPopulationGrowth,
    dataType
  });

  return scale.ticks;
};

// Helper to calculate the left position (as a percentage) for a tick value
const getTickLeft = (tickValue: number, minTick: number, range: number) => {
  // This is the same calculation as used for the vertical dashed lines
  return `${Math.round(((tickValue - minTick) / range) * 100 * 100) / 100}%`;
};

export const ChartCard: React.FC<ChartCardProps> = ({
  category,
  title,
  subtitle,
  mainValue,
  mainLocation,
  rank,
  data,
  xAxisLabel,
  yAxisLabels,
  rowIndex,
  cardIndex,
  onInfoClick,
}) => {
  const [isAboutDataOpen, setIsAboutDataOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadPopupOpen, setIsDownloadPopupOpen] = useState(false);
  const [isSharePopupOpen, setIsSharePopupOpen] = useState(false);
  const chartRef = useRef<HTMLDivElement>(null);
  const aboutData = chartsDataDefinitions[title];

  // Share link derived from current URL
  const shareLink = useShareUrl();

  // ------------------------------------------------------------
  // Existing single-chart download logic extracted to a new
  // function so we can call it from the modal callback.
  // ------------------------------------------------------------
  // chart card.tsx

  // ... (inside the ChartCard component)

  const downloadSingleChart = async () => {
    if (!chartRef.current) {
      console.log('No chart ref found');
      return;
    }
    
    console.log('Starting download for chart:', title);
    
    // =================================================================
    // REMOVE THE ENTIRE 'data.forEach' BLOCK. 
    // This logic attempts to manually shift labels and is the source
    // of the positioning error. We will handle this in chartDownload.ts.
    // =================================================================

    // Add download-shift-up class to all jurisdiction labels
    const labels = chartRef.current.querySelectorAll('.download-jurisdiction-label');
    labels.forEach(label => label.classList.add('download-shift-up'));

    // Shift rank bubble text up during download
    const rankText = chartRef.current.querySelector('.rank-bubble-text');
    if (rankText) rankText.classList.add('download-rank-bubble-text');

    const categoryTexts = chartRef.current.querySelectorAll('.category-bubble-text');
    categoryTexts.forEach(text => text.classList.add('download-category-bubble-text'));

    // Hide header action buttons during download
    const headerButtonsContainer = chartRef.current.querySelector<HTMLElement>('.chart-action-buttons');
    headerButtonsContainer?.classList.add('download-chart-headers-hide');
    
    // Inject style if not present
    let styleTag = document.getElementById('download-shift-up-style');
    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = 'download-shift-up-style';
      styleTag.innerHTML = `
        .download-shift-up {overflow: visible !important; position: relative !important; top: -8px !important;}
        .download-chart-headers-hide {display: none !important;}
      `;
      document.head.appendChild(styleTag);
    }
    setIsDownloading(true);
    try {
      // The download function will now handle all positioning.
      await downloadChart(chartRef.current, `${title.toLowerCase().replace(/\s+/g, '-')}`);
    } catch (error) {
      console.error('Error in handleDownload:', error);
    } finally {
      // Remove the class after download
      labels.forEach(label => label.classList.remove('download-shift-up'));

      if (rankText) rankText.classList.remove('download-rank-bubble-text');

      categoryTexts.forEach(text => text.classList.remove('download-category-bubble-text'));

      // Restore header buttons visibility
      headerButtonsContainer?.classList.remove('download-chart-headers-hide');

      setIsDownloading(false);
    }
  };
  // ------------------------------------------------------------
  // Helper to download every chart card on the page. Each chart
  // <article> gains a data-chart-title attribute (added below),
  // allowing us to query and iterate deterministically.
  // ------------------------------------------------------------
  const downloadAllCharts = async () => {
    console.log('[downloadAllCharts] Starting batch download');
    setIsDownloading(true);
    try {
      const articles = Array.from(document.querySelectorAll<HTMLElement>('article[data-chart-title]'));
      for (const el of articles) {
        const titleAttr = el.dataset.chartTitle || 'chart';
        await downloadChart(el, titleAttr.toLowerCase().replace(/\s+/g, '-'));
        // Slight delay ensures the browser writes each file sequentially
        await new Promise(res => setTimeout(res, 300));
      }
    } catch (error) {
      console.error('[downloadAllCharts] Error:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  // ------------------------------------------------------------
  // Handles user selection from the DownloadOptionsPopup modal.
  // ------------------------------------------------------------
  const handleDownloadOption = async (option: 'single' | 'all') => {
    setIsDownloadPopupOpen(false);
    if (option === 'single') {
      await downloadSingleChart();
    } else {
      await downloadAllCharts();
    }
  };

  // Clicking the icon now opens the modal instead of immediate download
  const handleDownloadButtonClick = () => {
    if (isDownloading) return; // guard against concurrent downloads
    setIsDownloadPopupOpen(true);
  };

  const headerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const { width: headerWidth, height: headerHeight } = useResizeObserver(headerRef);
  const { height: contentHeight } = useResizeObserver(contentRef);
  
  const { rowHeights, updateHeight, removeCard } = useChartRow();

  useEffect(() => {
    updateHeight(rowIndex, cardIndex, headerHeight);
  }, [headerHeight, updateHeight, rowIndex, cardIndex]);

  useEffect(() => {
    // Cleanup function
    return () => {
      removeCard(rowIndex, cardIndex);
    };
  }, [removeCard, rowIndex, cardIndex]);

  // Get the maximum height for this row
  const rowMaxHeight = rowHeights.get(rowIndex) || 0;

  // Calculate dynamic padding based on content
  const headerPadding = Math.max(6, Math.min(24, (180 - headerHeight) / 2));

  // Calculate tick values with safety check
  const tickValues = useMemo(() => calculateTicks(data, title), [data, title]);

  // Safety check for empty tickValues
  if (!tickValues?.length) {
    return (
      <article className="bg-white w-full min-w-[280px] pt-6 rounded-[20px] relative flex flex-col h-full">
        <div className="p-6">No data available</div>
      </article>
    );
  }

  // Calculate the range for proper scaling with safety checks
  const range = useMemo(() => {
    if (!tickValues?.length) return 1;
    const minTick = tickValues[0].value;
    const maxTick = tickValues[tickValues.length - 1].value;
    return maxTick - minTick || 1; // Prevent division by zero
  }, [tickValues]);

  // Calculate the zero position for the x-axis with safety checks
  const zeroPosition = useMemo(() => {
    if (!tickValues?.length) return 0;
    const minTick = tickValues[0].value;
    const maxTick = tickValues[tickValues.length - 1].value;
    if (minTick >= 0) return 0;
    if (maxTick <= 0) return 100;
    return (Math.abs(minTick) / (maxTick - minTick)) * 100;
  }, [tickValues]);

  // Refs and state for bar right edge measurement
  const barRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [barRights, setBarRights] = useState<number[]>([]);
  const firstColRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [firstColWidths, setFirstColWidths] = useState<number[]>([]);
  const chartAreaRef = useRef<HTMLDivElement>(null);
  // Add refs and state for label widths
  const labelRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [labelWidths, setLabelWidths] = useState<number[]>([]);

  // Helper function to calculate accurate fallback positioning
  const calculateFallbackPosition = useCallback((item: ChartData, index: number) => {
    // Account for grid layout: 180px first column + 16px gap + padding
    const firstColWidth = 180;
    const gap = 16;
    const padding = 8;
    const chartAreaPadding = 24; // px-6 = 24px
    
    // Calculate bar position using the same logic as the bars
    const barLeft = Math.min(((item.percentage || 0) - (tickValues[0]?.value || 0)) / range * 100, zeroPosition);
    const barWidth = Math.abs(((item.percentage || 0) - (tickValues[0]?.value || 0)) / range * 100 - zeroPosition);
    const barRightEdge = barLeft + barWidth;
    
    // Calculate the actual pixel position within the chart area
    const chartAreaWidth = 100 - (firstColWidth / (firstColWidth + gap + chartAreaPadding) * 100);
    const barRightPixel = (barRightEdge / 100) * chartAreaWidth;
    
    // Position relative to the chart area (after first column + gap)
    const labelLeft = firstColWidth + gap + barRightPixel;
    
    return {
      left: `${labelLeft}px`,
      transform: 'translateX(-100%)',
      textAlign: 'right' as const
    };
  }, [tickValues, range, zeroPosition]);

  // Helper function to calculate final positioning values
  const calculateFinalPosition = useCallback((item: ChartData, index: number) => {
    const barRight = barRights[index] ?? 0;
    const firstColWidth = firstColWidths[index] ?? 0;
    const labelWidth = labelWidths[index] ?? 0;
    const padding = 8;
    
    // If we have measurements, use them
    if (barRight > 0 && firstColWidth > 0) {
      // If the label would overflow into the y-axis label area
      if (barRight - labelWidth < firstColWidth + padding) {
        return {
          left: `${0}px`,
          transform: 'translateX(-15%)',
          textAlign: 'left' as const
        };
      } else {
        const calculatedLeft = Math.max(0, barRight - firstColWidth - 18);
        return {
          left: `${calculatedLeft}px`,
          transform: 'translateX(-100%)',
          textAlign: 'right' as const
        };
      }
    } else {
      // Use fallback calculation - inline to avoid circular dependency
      const firstColWidth = 180;
      const gap = 16;
      const chartAreaPadding = 24;
      
      const barLeft = Math.min(((item.percentage || 0) - (tickValues[0]?.value || 0)) / range * 100, zeroPosition);
      const barWidth = Math.abs(((item.percentage || 0) - (tickValues[0]?.value || 0)) / range * 100 - zeroPosition);
      const barRightEdge = barLeft + barWidth;
      
      const chartAreaWidth = 100 - (firstColWidth / (firstColWidth + gap + chartAreaPadding) * 100);
      const barRightPixel = (barRightEdge / 100) * chartAreaWidth;
      const labelLeft = firstColWidth + gap + barRightPixel;
      
      return {
        left: `${labelLeft}px`,
        transform: 'translateX(-100%)',
        textAlign: 'right' as const
      };
    }
  }, [barRights, firstColWidths, labelWidths, tickValues, range, zeroPosition]);

  useEffect(() => {
    if (!chartAreaRef.current) return;
    const chartAreaRect = chartAreaRef.current.getBoundingClientRect();
    const rights = barRefs.current.map(bar => {
      if (!bar) return 0;
      const rect = bar.getBoundingClientRect();
      // Return the right edge relative to the chart area
      return rect.right - chartAreaRect.left;
    });
    const widths = firstColRefs.current.map(col => {
      if (!col) return 0;
      return col.offsetWidth;
    });
    // Measure label widths
    const labelW = labelRefs.current.map(label => {
      if (!label) return 0;
      return label.offsetWidth;
    });
    setBarRights(rights);
    setFirstColWidths(widths);
    setLabelWidths(labelW);
    
    // Add resize listener to update positions on window resize
    const handleResize = () => {
      if (!chartAreaRef.current) return;
      const chartAreaRect = chartAreaRef.current.getBoundingClientRect();
      const rights = barRefs.current.map(bar => {
        if (!bar) return 0;
        const rect = bar.getBoundingClientRect();
        return rect.right - chartAreaRect.left;
      });
      const widths = firstColRefs.current.map(col => {
        if (!col) return 0;
        return col.offsetWidth;
      });
      const labelW = labelRefs.current.map(label => {
        if (!label) return 0;
        return label.offsetWidth;
      });
      setBarRights(rights);
      setFirstColWidths(widths);
      setLabelWidths(labelW);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [data, range, zeroPosition]);

  return (
    <>
    <article 
      ref={chartRef} 
      data-chart-title={title}
      className="bg-white w-full min-w-[280px] pt-6 rounded-[20px] relative flex flex-col h-full"
    >
      <header 
        ref={headerRef} 
        className="w-full px-6 relative max-md:max-w-full max-md:px-5 flex-shrink-0 flex flex-col after:content-[''] after:absolute after:bottom-0 after:inset-x-6 after:max-md:inset-x-5 after:h-[4px] after:bg-gradient-to-r after:from-[#CA0079] after:to-[#F1AB11] after:rounded-full"
        style={{ 
          minHeight: `${rowMaxHeight}px`,
          transition: 'min-height 0.2s ease-in-out',
          paddingBottom: `${headerPadding}px`
        }}
      >
        {/* Download and Share icons positioned in top right corner */}
        <div className="absolute top-0 right-6 flex items-center gap-4 chart-action-buttons">
          <button 
            aria-label="Download chart"
            onClick={handleDownloadButtonClick}
            disabled={isDownloading}
            className="relative"
          >
            {isDownloading ? (
              <LoadingSpinner size="sm" className="text-gray-600" />
            ) : (
              <span className="material-symbols-outlined w-6 h-6 text-2xl opacity-70 hover:opacity-100 transition-opacity">
                download
              </span>
            )}
          </button>
          <button aria-label="Share chart" onClick={() => setIsSharePopupOpen(true)}>
            <span className="material-symbols-outlined w-6 h-6 text-2xl opacity-70 hover:opacity-100 transition-opacity">
              share
            </span>
          </button>
          <button aria-label="About the Data" onClick={() => setIsAboutDataOpen(true)}>
            <span className="material-symbols-outlined w-6 h-6 text-2xl opacity-70 hover:opacity-100 transition-opacity">
              info
            </span>
          </button>
        </div>

        {/* Category callout positioned on the left */}
        <div className="w-full flex-shrink-0">
          <span className="gap-2.5 text-xs text-white font-medium whitespace-nowrap uppercase tracking-[0.48px] leading-[1.3] bg-[#CA0079] px-3 py-1.5 rounded-[20px] font-geist-mono inline-flex items-center justify-center category-bubble">
            <span className="category-bubble-text">{category}</span>
          </span>
        </div>
        
        <div className="flex w-full flex-col items-stretch text-[#212121] leading-[1.3] mt-5 max-md:max-w-full">
          <h3 className="text-[28px] font-semibold max-md:max-w-full font-geist">
            {title}
          </h3>
          <p className="text-xl font-normal mt-2.5 max-md:max-w-full font-geist opacity-70 min-h-[52px] pb-4">
            {subtitle}. Brought to you by Govstream.ai.
          </p>
        </div>
      </header>
      
      <div ref={contentRef} className="flex flex-col flex-1">
        <div className="w-full mt-3 px-6 max-md:px-5 flex-shrink-0">
          <div className="relative flex w-full items-start gap-4 chart-metric-row">
            <div className="flex flex-col flex-grow">
              <div className="text-[#212121] text-[48px] font-black font-geist tracking-tight leading-tight align-middle chart-text-shift" style={{lineHeight: '1.2', verticalAlign: 'middle'}}>
                {mainValue}
              </div>
              <div className="text-lg text-[#666666] mt-1 font-geist leading-tight align-middle chart-text-shift" style={{lineHeight: '1.2', verticalAlign: 'middle'}}>
                {mainLocation}
              </div>
            </div>
            <div className="bg-[#3B50A2] text-md text-white font-medium uppercase px-4 py-2 rounded-full flex-shrink-0 font-geist-mono leading-tight align-middle flex items-center justify-center self-start rank-bubble" style={{lineHeight: '1.2', verticalAlign: 'middle', marginTop: '0.5rem'}}>
              <span className="rank-bubble-text">{rank}</span>
            </div>
          </div>
        </div>
        
        {/* Chart area and x-axis label as a single flex column */}
        <div className="w-full mt-8 px-6 pr-8 max-md:px-5 flex flex-col">
          <div className="space-y-[20px] relative" ref={chartAreaRef}>
            {/* Chart bars with tick marks overlay */}
            <div className="relative">
              {/* Tick marks and grid lines - positioned over the right column only */}
              <div className="absolute left-[calc(180px)] right-0 top-0 bottom-0 z-10 pointer-events-none chart-ticks-container">
                {tickValues.map((tick, tickIndex) => (
                  <div
                    key={`tick-line-${tickIndex}`}
                    className="absolute h-full border-l border-dotted border-black/10"
                    style={{
                      left: getTickLeft(tick.value, tickValues[0]?.value || 0, range),
                    }}
                  />
                ))}
                {/* Add zero line if we have negative values */}
                {tickValues[0]?.value < 0 && tickValues[tickValues.length - 1]?.value > 0 && (
                  <div
                    className="absolute h-full border-l border-dotted border-black/10"
                    style={{
                      left: `${zeroPosition}%`,
                    }}
                  />
                )}
                {/* Tick labels rendered in the same container as tick lines */}
                {tickValues.map((tick, index) => (
                  <div
                    key={`tick-label-${index}`}
                    className="absolute text-sm font-medium font-geist-mono text-[#212121] leading-tight align-middle chart-tick-label"
                    style={{
                      lineHeight: '1.2',
                      verticalAlign: 'middle',
                      left: getTickLeft(tick.value, tickValues[0]?.value || 0, range),
                      bottom: '-1.5em', // place below the axis
                      transform: 'translateX(-41%)',
                      opacity: 0.7,
                      textAlign: 'left',
                    }}
                  >
                    {tick.label}
                  </div>
                ))}
              </div>
              
              {data.map((item, index) => (
                <div
                  key={index}
                  className={`w-full${index !== data.length - 1 ? ' mb-[20px]' : ''}`}
                >
                  <div className="flex flex-col gap-0.5">
                    <div className="grid grid-cols-[minmax(140px,180px)_3fr] gap-4 items-center">
                      <div
                        ref={el => firstColRefs.current[index] = el}
                        className={"text-sm font-medium font-geist-mono text-[#212121] transition-opacity duration-300 uppercase leading-tight align-left download-jurisdiction-label"}
                        style={{
                          opacity: item.name === mainLocation ? 1 : 0.7,
                          lineHeight: '1.3',
                          verticalAlign: 'middle'
                        }}
                      >
                        {item.name}
                      </div>
                      <div className="relative w-full flex items-center">
                        {/*
                          Edge case: If the label would overflow into the y-axis label area, left-align and position it just inside the chart area.
                          Otherwise, use the existing logic.
                        */}
                        <div
                          ref={el => labelRefs.current[index] = el}
                          className="absolute whitespace-nowrap transition-all duration-300 flex items-center download-chart-label bar-label-shift"
                          data-label-index={index}
                          data-label-value={item.value}
                          data-label-location={item.name}
                          style={(() => {
                            const barRight = barRights[index] ?? 0;
                            const firstColWidth = firstColWidths[index] ?? 0;
                            const labelWidth = labelWidths[index] ?? 0;
                            const padding = 8;
                            
                            if (barRight === 0 && firstColWidth === 0) {
                              // Use improved fallback calculation
                              const fallbackPosition = calculateFallbackPosition(item, index);
                              return {
                                ...fallbackPosition,
                                overflow: 'visible',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                opacity: item.name === mainLocation ? 1 : 0.7
                              };
                            }
                            
                            // If the label would overflow into the y-axis label area
                            if (barRight - labelWidth < firstColWidth + padding) {
                              return {
                                left: `${0}px`,
                                textAlign: 'left',
                                transform: 'translateX(-15%)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                opacity: item.name === mainLocation ? 1 : 0.7
                              };
                            } else {
                              const calculatedLeft = Math.max(0, barRight - firstColWidth - 18);
                              return {
                                left: `${calculatedLeft}px`,
                                textAlign: 'right',
                                transform: 'translateX(-100%)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                opacity: item.name === mainLocation ? 1 : 0.7
                              };
                            }
                          })()}
                        >
                          <span className="text-xs font-medium text-[#212121] font-geist-mono leading-none uppercase leading-tight align-middle chart-text-shift" style={{lineHeight: '1.2', verticalAlign: 'middle'}}>
                            {item.value}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-[minmax(140px,180px)_3fr] items-center">
                      <div /> {/* Empty space for alignment */}
                      <div className="w-full ml-[0%] h-3 bg-[#F5F5F5] rounded-full overflow-hidden relative chart-bar-track">
                        <div
                          ref={el => barRefs.current[index] = el}
                          className="h-full rounded-full transition-all duration-300 absolute"
                          style={{
                            backgroundColor: item.name === mainLocation ? '#A6006B' : '#F3BAD3',
                            left: `${Math.round(Math.min(((item.percentage || 0) - (tickValues[0]?.value || 0)) / range * 100, zeroPosition) * 100) / 100}%`,
                            width: `${Math.round(Math.abs(((item.percentage || 0) - (tickValues[0]?.value || 0)) / range * 100 - zeroPosition) * 100) / 100}%`,
                            right: (item.percentage || 0) >= 0 ? 'auto' : '0'
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
            {/* X-axis label, now aligned with the chart's data area using the same grid */}
            <div className="grid grid-cols-[minmax(140px,180px)_3fr] items-center mt-6 mb-6">
            {/* Empty first column to create the correct offset */}
            <div></div>
            {/* Second column containing the centered label */}
            <div className="chart-xaxis-label flex items-center justify-center text-xs text-[#666666] font-medium text-center uppercase font-geist-mono">
              {xAxisLabel}
            </div>
          </div>
        </div>
      </div>
    </article>
    {/* Download options modal */}
    <DownloadOptionsPopup
      isOpen={isDownloadPopupOpen}
      chartName={title}
      onClose={() => setIsDownloadPopupOpen(false)}
      onDownload={handleDownloadOption}
    />
    {/* Share options modal */}
    <ShareOptionsPopup
      isOpen={isSharePopupOpen}
      chartName={title}
      shareLink={shareLink}
      onClose={() => setIsSharePopupOpen(false)}
    />
    {isAboutDataOpen && aboutData && (
      <AboutDataCard
        onClose={() => setIsAboutDataOpen(false)}
        aboutDescription={subtitle}
        sources={aboutData.sources}
        metricDefinition={aboutData.metricDefinition}
        calculationFormula={aboutData.calculationFormula}
      />
    )}
    </>
  );
};